import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { BarChart3, FileText, MessageSquareMore, Send } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsApi } from '../../../api/reports.api';
import { customersApi } from '../../../api/customers.api';
import { templatesApi } from '../../../api/templates.api';

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value) || 0);

const toInputDate = (date) => {
  const nextDate = new Date(date);
  return Number.isNaN(nextDate.getTime()) ? '' : nextDate.toISOString().split('T')[0];
};

const buildDefaultFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);

  return {
    startDate: toInputDate(start),
    endDate: toInputDate(end),
    groupBy: 'day',
    customerId: 'all',
    messageType: 'all',
    templateId: 'all',
  };
};

const StatCard = ({ icon: Icon, label, value, helper, color }) => (
  <Card
    sx={{
      height: '100%',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ color, fontWeight: 800, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1 }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            bgcolor: 'rgba(34, 197, 94, 0.12)',
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={20} />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const SectionCard = ({ title, subtitle, children }) => (
  <Card
    sx={{
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
    }}
  >
    <CardContent>
      <Stack spacing={0.5} mb={2.5}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {subtitle}
          </Typography>
        ) : null}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const EmptyState = ({ label }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={1}
    sx={{
      minHeight: 220,
      border: '1px dashed',
      borderColor: 'divider',
      borderRadius: 3,
      color: '#64748b',
      textAlign: 'center',
      px: 3,
    }}
  >
    <BarChart3 size={40} />
    <Typography>{label}</Typography>
  </Stack>
);

const ReportsPage = () => {
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadOptions = async () => {
      try {
        const [customerResponse, templateResponse] = await Promise.all([
          customersApi.getAll(),
          templatesApi.getAll(),
        ]);

        if (!cancelled) {
          setCustomers(customerResponse?.data?.data || []);
          setTemplates(templateResponse?.data?.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load report filters:', error);
          setCustomers([]);
          setTemplates([]);
        }
      }
    };

    loadOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      setLoading(true);
      try {
        const params = {
          startDate: filters.startDate,
          endDate: filters.endDate,
          groupBy: filters.groupBy,
        };

        if (filters.customerId !== 'all') params.customerId = filters.customerId;
        if (filters.messageType !== 'all') params.messageType = filters.messageType;
        if (filters.templateId !== 'all') params.templateId = filters.templateId;

        const response = await reportsApi.getMessageDashboard(params);
        if (!cancelled) {
          setDashboard(response?.data?.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load message reports dashboard:', error);
          setDashboard(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const summaryCards = useMemo(() => {
    const summary = dashboard?.summary || {};

    return [
      {
        label: 'Total Messages Sent',
        value: formatNumber(summary.totalSent),
        color: '#16a34a',
        icon: Send,
      },
      {
        label: 'Total Messages Received',
        value: formatNumber(summary.totalReceived),
        color: '#2563eb',
        icon: MessageSquareMore,
      },
      {
        label: 'Total Messages Delivered',
        value: formatNumber(summary.totalDelivered),
        color: '#0f766e',
        icon: FileText,
      },
      {
        label: 'Total Messages Read',
        value: formatNumber(summary.totalRead),
        color: '#0284c7',
        icon: BarChart3,
      },
      {
        label: 'Total Messages Failed',
        value: formatNumber(summary.totalFailed),
        color: '#dc2626',
        icon: FileText,
      },
    ];
  }, [dashboard]);

  const trendRows = dashboard?.trends || [];
  const customerRows = dashboard?.customerReport || [];
  const typeRows = dashboard?.messageTypeReport || [];
  const templateUsage = dashboard?.templateUsage || {
    summary: {},
    perTemplate: [],
    perCustomer: [],
    templateCustomerUsage: [],
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: '#f8fafc', p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background:
              'linear-gradient(135deg, rgba(15,118,110,0.10) 0%, rgba(34,197,94,0.08) 45%, rgba(255,255,255,1) 100%)',
            boxShadow: '0 18px 50px rgba(15, 23, 42, 0.06)',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack spacing={2.5}>
              <Stack spacing={1}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  Message Reports Dashboard
                </Typography>
                <Typography sx={{ color: '#475569', maxWidth: 900 }}>
                  Analytics for WhatsApp message volume, delivery status, customer activity, message types, and template usage with daily, monthly, and yearly trend views.
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={filters.startDate}
                    onChange={handleFilterChange('startDate')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={filters.endDate}
                    onChange={handleFilterChange('endDate')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="group-by-label">Trend View</InputLabel>
                    <Select
                      labelId="group-by-label"
                      label="Trend View"
                      value={filters.groupBy}
                      onChange={handleFilterChange('groupBy')}
                    >
                      <MenuItem value="day">Daily</MenuItem>
                      <MenuItem value="month">Monthly</MenuItem>
                      <MenuItem value="year">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="customer-filter-label">Customer</InputLabel>
                    <Select
                      labelId="customer-filter-label"
                      label="Customer"
                      value={filters.customerId}
                      onChange={handleFilterChange('customerId')}
                    >
                      <MenuItem value="all">All Customers</MenuItem>
                      {customers.map((customer) => (
                        <MenuItem key={customer.cd_id} value={String(customer.cd_id)}>
                          {customer.cd_name || customer.cd_number} {customer.cd_number ? `(${customer.cd_number})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1}>
                  <FormControl fullWidth>
                    <InputLabel id="message-type-filter-label">Type</InputLabel>
                    <Select
                      labelId="message-type-filter-label"
                      label="Type"
                      value={filters.messageType}
                      onChange={handleFilterChange('messageType')}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="service">Service</MenuItem>
                      <MenuItem value="template">Template</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="template-filter-label">Template</InputLabel>
                    <Select
                      labelId="template-filter-label"
                      label="Template"
                      value={filters.templateId}
                      onChange={handleFilterChange('templateId')}
                    >
                      <MenuItem value="all">All</MenuItem>
                      {templates.map((template) => (
                        <MenuItem key={template.t_id} value={String(template.t_id)}>
                          {template.t_tname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              {summaryCards.map((card) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={card.label}>
                  <StatCard {...card} />
                </Grid>
              ))}
            </Grid>

            <SectionCard
              title="Message Trend"
              subtitle="Trend chart for sent, received, delivered, read, and failed messages based on the selected day, month, or year grouping."
            >
              {trendRows.length === 0 ? (
                <EmptyState label="No message trend data found for the selected filters." />
              ) : (
                <Box sx={{ height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendRows}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" tickLine={false} axisLine={false} />
                      <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="sent" fill="#16a34a" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="received" fill="#2563eb" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="delivered" fill="#0f766e" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="read" fill="#0284c7" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="failed" fill="#dc2626" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </SectionCard>

            <SectionCard
              title="Customer-wise Message Report"
              subtitle="Customer-level message activity showing sent, received, delivered, read, and failed totals."
            >
              {customerRows.length === 0 ? (
                <EmptyState label="No customer message activity found for the selected filters." />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell align="right">Sent</TableCell>
                        <TableCell align="right">Received</TableCell>
                        <TableCell align="right">Delivered</TableCell>
                        <TableCell align="right">Read</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customerRows.map((row) => (
                        <TableRow key={`${row.customerId || row.phoneNumber}-${row.customerName}`}>
                          <TableCell>{row.customerName}</TableCell>
                          <TableCell>{row.phoneNumber || '-'}</TableCell>
                          <TableCell align="right">{formatNumber(row.sent)}</TableCell>
                          <TableCell align="right">{formatNumber(row.received)}</TableCell>
                          <TableCell align="right">{formatNumber(row.delivered)}</TableCell>
                          <TableCell align="right">{formatNumber(row.read)}</TableCell>
                          <TableCell align="right">{formatNumber(row.failed)}</TableCell>
                          <TableCell align="right">{formatNumber(row.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>

            <SectionCard
              title="Message Type Report"
              subtitle="Breakdown of service messages and template messages with status-wise totals."
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Message Type</TableCell>
                      <TableCell align="right">Sent</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell align="right">Delivered</TableCell>
                      <TableCell align="right">Read</TableCell>
                      <TableCell align="right">Failed</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {typeRows.map((row) => (
                      <TableRow key={row.type}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right">{formatNumber(row.sent)}</TableCell>
                        <TableCell align="right">{formatNumber(row.received)}</TableCell>
                        <TableCell align="right">{formatNumber(row.delivered)}</TableCell>
                        <TableCell align="right">{formatNumber(row.read)}</TableCell>
                        <TableCell align="right">{formatNumber(row.failed)}</TableCell>
                        <TableCell align="right">{formatNumber(row.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <StatCard
                  icon={FileText}
                  label="Template Total Sent"
                  value={formatNumber(templateUsage.summary?.totalRecords)}
                  color="#0f172a"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <StatCard
                  icon={Send}
                  label="Template Delivered"
                  value={formatNumber(templateUsage.summary?.delivered)}
                  color="#16a34a"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <StatCard
                  icon={BarChart3}
                  label="Template Read"
                  value={formatNumber(templateUsage.summary?.read)}
                  color="#0284c7"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <StatCard
                  icon={FileText}
                  label="Template Failed"
                  value={formatNumber(templateUsage.summary?.failed)}
                  color="#dc2626"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <StatCard
                  icon={MessageSquareMore}
                  label="Template Customers"
                  value={formatNumber(templateUsage.summary?.uniqueCustomers)}
                  color="#0f766e"
                />
              </Grid>
            </Grid>

            <SectionCard
              title="Template Usage Report"
              subtitle="Template-wise usage totals showing sent, delivered, read, failed, and customer reach."
            >
              {templateUsage.perTemplate?.length ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Template</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Language</TableCell>
                        <TableCell align="right">Sent</TableCell>
                        <TableCell align="right">Delivered</TableCell>
                        <TableCell align="right">Read</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell align="right">Customers</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {templateUsage.perTemplate.map((row) => (
                        <TableRow key={row.templateId}>
                          <TableCell>{row.templateName}</TableCell>
                          <TableCell>{row.category || '-'}</TableCell>
                          <TableCell>{row.language || '-'}</TableCell>
                          <TableCell align="right">{formatNumber(row.totalSent)}</TableCell>
                          <TableCell align="right">{formatNumber(row.delivered)}</TableCell>
                          <TableCell align="right">{formatNumber(row.read)}</TableCell>
                          <TableCell align="right">{formatNumber(row.failed)}</TableCell>
                          <TableCell align="right">{formatNumber(row.uniqueCustomers)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyState label="No template usage records found for the selected filters." />
              )}
            </SectionCard>

            <Grid container spacing={3}>
              <Grid item xs={12} xl={5}>
                <SectionCard
                  title="Customer-wise Template Usage"
                  subtitle="How many template sends and statuses each customer received."
                >
                  {templateUsage.perCustomer?.length ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Customer</TableCell>
                            <TableCell>Phone Number</TableCell>
                            <TableCell align="right">Sent</TableCell>
                            <TableCell align="right">Delivered</TableCell>
                            <TableCell align="right">Read</TableCell>
                            <TableCell align="right">Failed</TableCell>
                            <TableCell align="right">Templates</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {templateUsage.perCustomer.map((row) => (
                            <TableRow key={row.customerId}>
                              <TableCell>{row.customerName}</TableCell>
                              <TableCell>{row.customerNumber || '-'}</TableCell>
                              <TableCell align="right">{formatNumber(row.totalSent)}</TableCell>
                              <TableCell align="right">{formatNumber(row.delivered)}</TableCell>
                              <TableCell align="right">{formatNumber(row.read)}</TableCell>
                              <TableCell align="right">{formatNumber(row.failed)}</TableCell>
                              <TableCell align="right">{formatNumber(row.uniqueTemplates)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <EmptyState label="No customer-wise template usage data found." />
                  )}
                </SectionCard>
              </Grid>
              <Grid item xs={12} xl={7}>
                <SectionCard
                  title="Repeated Template Sends"
                  subtitle="How many times the same template was sent to the same customer."
                >
                  {templateUsage.templateCustomerUsage?.length ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Template</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Phone Number</TableCell>
                            <TableCell align="right">Send Count</TableCell>
                            <TableCell align="right">Delivered</TableCell>
                            <TableCell align="right">Read</TableCell>
                            <TableCell align="right">Failed</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {templateUsage.templateCustomerUsage.map((row) => (
                            <TableRow key={`${row.templateId}-${row.customerId}`}>
                              <TableCell>{row.templateName}</TableCell>
                              <TableCell>{row.customerName}</TableCell>
                              <TableCell>{row.customerNumber || '-'}</TableCell>
                              <TableCell align="right">{formatNumber(row.sendCount)}</TableCell>
                              <TableCell align="right">{formatNumber(row.delivered)}</TableCell>
                              <TableCell align="right">{formatNumber(row.read)}</TableCell>
                              <TableCell align="right">{formatNumber(row.failed)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <EmptyState label="No repeated template send data found." />
                  )}
                </SectionCard>
              </Grid>
            </Grid>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default ReportsPage;
